import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Row,
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../Components/Loader';
import Message from '../../Components/Message';
import BackButton from '../../Components/BackButton';
import {
  useAdminConvertQuoteToInvoiceMutation,
  useAdminGetQuoteQuery,
} from '../../slices/quotesApiSlice';
import { toast } from 'react-toastify';
import { useGetUsersQuery } from '../../slices/usersApiSlice';
import { getTotalItemQuantity } from '../../utils/itemCount';

const QuoteConvertScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    data: quote,
    isLoading,
    error,
  } = useAdminGetQuoteQuery(id);

  const [convert, { isLoading: isConverting }] =
    useAdminConvertQuoteToInvoiceMutation();
  const { data: users = [], isLoading: isLoadingUsers } = useGetUsersQuery();

  const [fulfillmentMethod, setFulfillmentMethod] =
    useState('ship');
  const [paymentMethod, setPaymentMethod] =
    useState('POS');
  const [markPaid, setMarkPaid] = useState(false);
  const [pickupLocation, setPickupLocation] =
    useState('Ottawa Store (114, 15 Capella Ct, ON, Canada)');
  const [shippingFullName, setShippingFullName] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingAddress2, setShippingAddress2] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingProvince, setShippingProvince] = useState('ON');
  const [shippingPostalCode, setShippingPostalCode] = useState('');
  const [shippingCountry, setShippingCountry] = useState('CA');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerUserId, setCustomerUserId] = useState('');

  const customerOptions = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    return users.filter((user) => {
      if (!term) return true;
      return `${user.name || ''} ${user.email || ''}`.toLowerCase().includes(term);
    });
  }, [users, customerSearch]);

  const quoteNumber = useMemo(
    () => quote?.number || quote?.quoteNumber || quote?._id,
    [quote]
  );

  useEffect(() => {
    if (!quote) return;
    if (quote.user && !customerUserId) {
      setCustomerUserId(String(quote.user?._id || quote.user));
    }
    if (!shippingFullName) {
      setShippingFullName(quote.customerName || '');
    }
    if (!shippingPhone) {
      setShippingPhone(quote.customerPhone || '');
    }
  }, [quote, shippingFullName, shippingPhone, customerUserId]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (fulfillmentMethod === 'ship') {
      const normalizedCountry = String(shippingCountry || '')
        .trim()
        .toUpperCase();
      const needsState = ['CA', 'US'].includes(normalizedCountry);
      if (
        !shippingFullName.trim() ||
        !shippingPhone.trim() ||
        !shippingAddress.trim() ||
        !shippingCity.trim() ||
        !shippingPostalCode.trim() ||
        !normalizedCountry
      ) {
        toast.error('Please complete all required shipping fields.');
        return;
      }
      if (needsState && !String(shippingProvince || '').trim()) {
        toast.error('Please enter a province/state for shipping.');
        return;
      }
    }

    try {
      const res = await convert({
        id,
        body: {
          paymentMethod,
          fulfillmentMethod,
          shippingAddress:
            fulfillmentMethod === 'ship'
              ? {
                  fullName: shippingFullName.trim(),
                  phone: shippingPhone.trim(),
                  address: shippingAddress.trim(),
                  address2: shippingAddress2.trim(),
                  city: shippingCity.trim(),
                  province: String(shippingProvince || '')
                    .trim()
                    .toUpperCase(),
                  postalCode: shippingPostalCode
                    .replace(/\s+/g, '')
                    .toUpperCase(),
                  country: String(shippingCountry || '')
                    .trim()
                    .toUpperCase(),
                }
              : null,
          pickupLocation:
            fulfillmentMethod === 'pickup'
              ? pickupLocation
              : null,
          markPaid,
          userId: customerUserId || null,
        },
      }).unwrap();
      toast.success('Order created from quote.');
      navigate(`/order/${res.orderId}`);
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.message ||
          'Failed to convert quote.'
      );
    }
  };

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1 className="mb-0">Convert Quote</h1>
          <div className="text-muted">
            Quote {quoteNumber}
          </div>
        </Col>
        <Col className="text-end">
          <BackButton
            to="/admin/quotes"
            variant="outline-secondary"
            className=""
          >
            Back to Quotes
          </BackButton>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || error?.error}
        </Message>
      ) : (
        <Form onSubmit={submitHandler}>
          <Row>
            <Col md={7}>
              <Card className="mb-3">
                <Card.Body>
                  <Card.Title className="mb-3">
                    Shipping Method
                  </Card.Title>
                  <Form.Check
                    type="radio"
                    id="fulfillment-ship"
                    name="fulfillmentMethod"
                    label="Ship to customer"
                    value="ship"
                    checked={fulfillmentMethod === 'ship'}
                    onChange={(e) =>
                      setFulfillmentMethod(e.target.value)
                    }
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="fulfillment-pickup"
                    name="fulfillmentMethod"
                    label="Pickup in store"
                    value="pickup"
                    checked={fulfillmentMethod === 'pickup'}
                    onChange={(e) =>
                      setFulfillmentMethod(e.target.value)
                    }
                    className="mb-3"
                  />
                  {fulfillmentMethod === 'pickup' && (
                    <Form.Group controlId="pickupLocation">
                      <Form.Label>
                        Pickup location
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={pickupLocation}
                        onChange={(e) =>
                          setPickupLocation(e.target.value)
                        }
                      />
                    </Form.Group>
                  )}
                </Card.Body>
              </Card>
              {fulfillmentMethod === 'ship' && (
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title className="mb-3">
                      Shipping Details
                    </Card.Title>
                    <Row>
                      <Col md={6}>
                        <Form.Group
                          className="mb-3"
                          controlId="shippingFullName"
                        >
                          <Form.Label>
                            Full name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={shippingFullName}
                            onChange={(e) =>
                              setShippingFullName(e.target.value)
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group
                          className="mb-3"
                          controlId="shippingPhone"
                        >
                          <Form.Label>
                            Phone
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={shippingPhone}
                            onChange={(e) =>
                              setShippingPhone(e.target.value)
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group
                      className="mb-3"
                      controlId="shippingAddress"
                    >
                      <Form.Label>
                        Address
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={shippingAddress}
                        onChange={(e) =>
                          setShippingAddress(e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                    <Form.Group
                      className="mb-3"
                      controlId="shippingAddress2"
                    >
                      <Form.Label>
                        Address 2 (optional)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={shippingAddress2}
                        onChange={(e) =>
                          setShippingAddress2(e.target.value)
                        }
                      />
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                        <Form.Group
                          className="mb-3"
                          controlId="shippingCity"
                        >
                          <Form.Label>
                            City
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={shippingCity}
                            onChange={(e) =>
                              setShippingCity(e.target.value)
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group
                          className="mb-3"
                          controlId="shippingProvince"
                        >
                          <Form.Label>
                            Province/State
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={shippingProvince}
                            onChange={(e) =>
                              setShippingProvince(e.target.value)
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group
                          className="mb-3"
                          controlId="shippingPostalCode"
                        >
                          <Form.Label>
                            Postal code
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={shippingPostalCode}
                            onChange={(e) =>
                              setShippingPostalCode(e.target.value)
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group
                          className="mb-3"
                          controlId="shippingCountry"
                        >
                          <Form.Label>
                            Country
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={shippingCountry}
                            onChange={(e) =>
                              setShippingCountry(e.target.value)
                            }
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </Col>
            <Col md={5}>
              <Card className="mb-3">
                <Card.Body>
                  <Card.Title className="mb-3">Customer Account</Card.Title>
                  <Form.Group className="mb-2" controlId="customerSearch">
                    <Form.Label>Search registered customers</Form.Label>
                    <Form.Control
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Name or email"
                    />
                  </Form.Group>
                  <Form.Group controlId="customerUserId">
                    <Form.Label>Linked customer account</Form.Label>
                    <Form.Select
                      value={customerUserId}
                      onChange={(e) => setCustomerUserId(e.target.value)}
                      disabled={isLoadingUsers}
                    >
                      <option value="">No registered account</option>
                      {customerOptions.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.name} — {user.email}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Optional. The finalized order will appear in this customer's My Orders.
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Body>
                  <Card.Title className="mb-3">
                    Payment Method
                  </Card.Title>
                  <Form.Group controlId="paymentMethod">
                    <Form.Label>
                      Select payment method
                    </Form.Label>
                  <Form.Select
                    value={paymentMethod}
                    onChange={(e) =>
                      setPaymentMethod(e.target.value)
                    }
                    >
                      <option value="POS">
                        POS
                      </option>
                      <option value="PayPal">
                        PayPal
                      </option>
                      <option value="Pay on Pickup">
                        Pay on Pickup
                      </option>
                      <option value="Payment at Delivery">
                        Payment at Delivery
                      </option>
                      <option value="End of Month Payment">
                        End of Month Payment
                      </option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Check
                    className="mt-3"
                    type="checkbox"
                    id="markPaid"
                    label="Mark order as paid"
                    checked={markPaid}
                    onChange={(e) =>
                      setMarkPaid(e.target.checked)
                    }
                  />
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <Card.Title className="mb-3">
                    Quote Summary
                  </Card.Title>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="px-0">
                      <Row>
                        <Col>Subtotal</Col>
                        <Col className="text-end">
                          {Number(quote?.subtotal ?? 0).toFixed(2)}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0">
                      <Row>
                        <Col>Tax</Col>
                        <Col className="text-end">
                          {Number(quote?.tax ?? 0).toFixed(2)}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0">
                      <Row>
                        <Col>Total Items</Col>
                        <Col className="text-end">
                          {getTotalItemQuantity(quote?.items)}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0">
                      <Row>
                        <Col>Total</Col>
                        <Col className="text-end fw-semibold">
                          {Number(quote?.total ?? 0).toFixed(2)}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              type="submit"
              variant="primary"
              disabled={isConverting}
            >
              {isConverting
                ? 'Converting...'
                : 'Create Order'}
            </Button>
          </div>
        </Form>
      )}
    </>
  );
};

export default QuoteConvertScreen;
